import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatMoney, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gauge, Printer, Loader2 } from "lucide-react";

export default function QuotationPrint() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/quotations/${id}`).then(({ data }) => setQ(data)).catch(() => setError("Quotation not found or access denied."));
  }, [id]);

  if (error) return <div className="p-10 text-center text-sm text-red-500" data-testid="print-error">{error}</div>;
  if (!q) return <div className="p-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="quotation-print-page">
      <div className="no-print sticky top-0 flex items-center justify-between border-b bg-slate-900 px-6 py-3 text-white">
        <span className="text-sm">Print preview — {q.quotation_no}</span>
        <Button size="sm" onClick={() => window.print()} data-testid="print-quotation-button">
          <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
        </Button>
      </div>
      <div className="mx-auto max-w-3xl p-10 print:p-0">
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-6">
          <div className="flex items-center gap-3">
            <Gauge className="h-9 w-9 text-blue-700" />
            <div>
              <div className="font-display text-2xl font-bold tracking-tight">BMDLabsystem</div>
              <div className="text-xs text-slate-500">ISO/IEC 17025 Accredited Calibration Laboratory</div>
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="font-mono font-semibold">{q.quotation_no}</div>
            <div className="text-slate-500">Revision v{q.version}</div>
            <div className="text-slate-500">{formatDate(q.created_at)}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Quotation To</div>
            <div className="font-semibold">{q.customer?.company_name}</div>
            <div>{q.customer?.contact_person}</div>
            <div className="whitespace-pre-line text-slate-600">{q.customer?.address}</div>
            <div className="text-slate-600">{q.customer?.email}</div>
            {q.customer?.tax_id && <div className="mt-1 font-mono text-xs">Tax ID: {q.customer.tax_id}</div>}
          </div>
          <div className="text-right">
            {q.valid_until && (
              <>
                <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Valid Until</div>
                <div className="font-semibold">{formatDate(q.valid_until)}</div>
              </>
            )}
          </div>
        </div>

        <table className="mt-8 w-full border-collapse text-sm">
          <thead>
            <tr className="border-y-2 border-slate-900 text-left">
              <th className="py-2 pr-4">Service Description</th>
              <th className="w-16 py-2 text-center">Qty</th>
              <th className="w-28 py-2 text-right">Unit Price</th>
              <th className="w-28 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {q.items.map((item, i) => (
              <tr key={i} className="border-b border-slate-200">
                <td className="py-2.5 pr-4">{item.description}</td>
                <td className="py-2.5 text-center font-mono">{item.quantity}</td>
                <td className="py-2.5 text-right font-mono">{formatMoney(item.unit_price, q.currency)}</td>
                <td className="py-2.5 text-right font-mono">{formatMoney(item.quantity * item.unit_price, q.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="ml-auto mt-4 w-72 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">{formatMoney(q.subtotal, q.currency)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Discount ({q.discount_percent}%)</span><span className="font-mono">-{formatMoney(q.discount_amount, q.currency)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Tax ({q.tax_percent}%)</span><span className="font-mono">{formatMoney(q.tax_amount, q.currency)}</span></div>
          <div className="flex justify-between border-t-2 border-slate-900 pt-2 text-base font-bold">
            <span>Total</span><span className="font-mono" data-testid="print-total">{formatMoney(q.total, q.currency)}</span>
          </div>
        </div>

        {q.notes && (
          <div className="mt-10 text-sm">
            <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Terms & Notes</div>
            <p className="text-slate-600">{q.notes}</p>
          </div>
        )}

        <div className="mt-16 grid grid-cols-2 gap-10 text-sm">
          <div className="border-t border-slate-400 pt-2 text-slate-500">Prepared by — {q.created_by}</div>
          <div className="border-t border-slate-400 pt-2 text-right text-slate-500">Customer Acceptance (Sign / Date)</div>
        </div>
        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-widest text-slate-400">
          BMDLabsystem · Traceable to national/international standards · ISO/IEC 17025:2017
        </p>
      </div>
    </div>
  );
}
